import { OpenFeatureEventEmitter, ProviderEvents, ProviderNotReadyError, TypeMismatchError, ParseError } from '@openfeature/web-sdk';
import { isType, transformContext, parseError, toResolutionDetails } from '@openfeature/config-cat-core';
import { getClient, PollingMode, ClientCacheState } from 'configcat-js-ssr';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

class ConfigCatWebProvider {
    constructor(clientFactory) {
        this.events = new OpenFeatureEventEmitter();
        this._isProviderReady = false;
        this.runsOn = 'client';
        this.metadata = {
            name: ConfigCatWebProvider.name,
        };
        this._clientFactory = clientFactory;
    }
    static create(sdkKey, options) {
        // Let's create a shallow copy to not mess up caller's options object.
        options = options ? Object.assign({}, options) : {};
        return new ConfigCatWebProvider((provider) => {
            const oldSetupHooks = options === null || options === void 0 ? void 0 : options.setupHooks;
            options.setupHooks = (hooks) => {
                oldSetupHooks === null || oldSetupHooks === void 0 ? void 0 : oldSetupHooks(hooks);
                hooks.on('configChanged', (config) => provider.events.emit(ProviderEvents.ConfigurationChanged, {
                    flagsChanged: Object.keys(config.settings),
                }));
            };
            return getClient(sdkKey, PollingMode.AutoPoll, options);
        });
    }
    initialize() {
        return __awaiter(this, void 0, void 0, function* () {
            const client = this._clientFactory(this);
            const clientCacheState = yield client.waitForReady();
            this._client = client;
            if (clientCacheState !== ClientCacheState.NoFlagData) {
                this._isProviderReady = true;
            }
            else {
                // OpenFeature provider defines ready state like this: "The provider is ready to resolve flags."
                // However, ConfigCat client's behavior is different: in some cases ready state may be reached
                // even if the client's internal, in-memory cache hasn't been populated yet, that is,
                // the client is not able to evaluate feature flags yet. In such cases we throw an error to
                // prevent the provider from being set ready right away, and check for the ready state later.
                throw Error('The underlying ConfigCat client could not initialize within maxInitWaitTimeSeconds.');
            }
        });
    }
    get configCatClient() {
        return this._client;
    }
    onClose() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            (_a = this._client) === null || _a === void 0 ? void 0 : _a.dispose();
        });
    }
    resolveBooleanEvaluation(flagKey, defaultValue, context) {
        return this.evaluate(flagKey, 'boolean', defaultValue, context);
    }
    resolveStringEvaluation(flagKey, defaultValue, context) {
        return this.evaluate(flagKey, 'string', defaultValue, context);
    }
    resolveNumberEvaluation(flagKey, defaultValue, context) {
        return this.evaluate(flagKey, 'number', defaultValue, context);
    }
    resolveObjectEvaluation(flagKey, defaultValue, context) {
        const objectValue = this.evaluate(flagKey, 'object', defaultValue, context);
        return objectValue;
    }
    evaluate(flagKey, flagType, defaultValue, context) {
        if (!this._client) {
            throw new ProviderNotReadyError('Provider is not initialized');
        }
        // Make sure that the user-provided `defaultValue` is compatible with `flagType` as there is
        // no guarantee that it actually is. (User may bypass type checking or may not use TypeScript at all.)
        if (!isType(flagType, defaultValue)) {
            throw new TypeMismatchError();
        }
        const configCatDefaultValue = flagType !== 'object' ? defaultValue : JSON.stringify(defaultValue);
        const snapshot = this._client.snapshot();
        const _a = snapshot.getValueDetails(flagKey, configCatDefaultValue, transformContext(context)), { value } = _a, evaluationData = __rest(_a, ["value"]);
        if (!this._isProviderReady && snapshot.cacheState !== ClientCacheState.NoFlagData) {
            // Ideally, we would check ConfigCat client's initialization state in its "background" polling loop.
            // This is not possible at the moment, so as a workaround, we do the check on feature flag evaluation.
            // There are plans to improve this situation, so let's revise this
            // as soon as ConfigCat SDK implements the necessary event.
            this._isProviderReady = true;
            setTimeout(() => this.events.emit(ProviderEvents.Ready), 0);
        }
        if (evaluationData.isDefaultValue) {
            throw parseError(evaluationData.errorMessage);
        }
        if (flagType !== 'object') {
            // When `flagType` (more precisely, `configCatDefaultValue`) is boolean, string or number,
            // ConfigCat SDK guarantees that the returned `value` is compatible with `PrimitiveType<T>`.
            // See also: https://configcat.com/docs/sdk-reference/js-ssr/#setting-type-mapping
            return toResolutionDetails(value, evaluationData);
        }
        let json;
        try {
            // In this case we can be sure that `value` is string since `configCatDefaultValue` is string,
            // which means that ConfigCat SDK is guaranteed to return a string value.
            json = JSON.parse(value);
        }
        catch (e) {
            throw new ParseError(`Unable to parse "${value}" as JSON`);
        }
        return toResolutionDetails(json, evaluationData);
    }
}

export { ConfigCatWebProvider };
